import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { api } from "../lib/api";
import { DataTable, type Column } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import type { ReportRun } from "@sbd-tcic/shared-types";

/**
 * Daily Operational Reporting (CMP-015 / FR-DASH-006…009). Backed by the real `report_runs` D1
 * table the nightly job in apps/jobs/src/daily-reports.ts writes to, via `GET /reports/runs`.
 */
const KNOWN_REPORT_TYPES_WITH_REAL_DATA = ["Open Order/Open Delivery Report", "Back Ordered Parts List", "Negative Inventory Reports", "Stock in Transit Report"];
const KNOWN_REPORT_TYPES_NO_DATA_SOURCE = ["COGI Errors", "TBWO Report", "Discontinued Parts on Order Report", "Warehouse Shipping Report", "Incompletion Log Report", "Delivery Block Reports"];

export function Reports() {
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api.get<ReportRun[]>("/reports/runs").then((data) => {
      setRuns(data);
      setLoading(false);
    });
  }, []);

  const columns: Column<ReportRun>[] = [
    { key: "date", header: "Run Date", render: (r) => r.runDate },
    { key: "type", header: "Report Type", render: (r) => r.reportType },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status === "success" ? "Complete" : "Fail"} /> },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="card p-5">
        <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3.5">Report Runs</h2>
        {loading ? (
          <div className="text-sm text-ink-muted text-center py-8">Loading…</div>
        ) : (
          <DataTable columns={columns} rows={runs} rowKey={(r) => r.id} emptyMessage="No report runs recorded yet — the nightly job hasn't run in this environment." />
        )}
      </div>

      <div className="card p-5">
        <h3 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3 flex items-center gap-2">
          <Info size={14} /> What each report type actually measures
        </h3>
        <p className="text-xs text-ink-muted mb-3.5">
          Four of the ten nightly report types have a real, computable equivalent in this schema. The remaining six are
          SAP/WMS-specific report types with no equivalent data source here — the nightly job honestly records a zero
          rather than inventing a figure for those (see 07_Quality_Test_And_Validation.md).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-status-green mb-1.5">Backed by real computed figures</div>
            <ul className="text-sm flex flex-col gap-1">
              {KNOWN_REPORT_TYPES_WITH_REAL_DATA.map((t) => (
                <li key={t} className="text-ink">
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink-muted mb-1.5">No equivalent data source in this schema</div>
            <ul className="text-sm flex flex-col gap-1">
              {KNOWN_REPORT_TYPES_NO_DATA_SOURCE.map((t) => (
                <li key={t} className="text-ink-muted">
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
